import { useEffect, useState } from 'react';
import { operatorConsole, PoolOfferConfig, type OperatorMe } from '../../lib/api/operatorConsole';
import { PackOpeningModal } from '../Animation/PackOpeningModal';
import { ImageSlot } from './ImageSlot';
import { TextSetting } from './TextSetting';
import { DemoToggle } from './DemoToggle';
import { GlobalPricingBar } from './GlobalPricingBar';
import { OpeningAnimationSetting } from './OpeningAnimationSetting';
import { PricingTable } from './PricingTable';
import { PublishControl } from './PublishControl';
import { usePackOpenings } from './usePackOpenings';
import {
  buybackEffective,
  commonValue,
  hasFixedRates,
  isCustomPack,
  priceLabel,
  refusedLevel,
  restockingHint,
  rtpEffective,
  type FixedRatesResult,
  type PricingPatch,
} from './pricing';

type TabId = 'pricing' | 'assets';

const TABS: { id: TabId; label: string }[] = [
  { id: 'assets', label: 'Assets' },
  { id: 'pricing', label: 'Pricing' },
];

const TAB_STORAGE_KEY = 'operator-console.packs-tab';

function readStoredTab(): TabId {
  try {
    return localStorage.getItem(TAB_STORAGE_KEY) === 'pricing' ? 'pricing' : 'assets';
  } catch {
    return 'assets';
  }
}

const IMAGE_SLOTS = [
  { key: 'bg_image', label: 'Background' },
  { key: 'pack_image', label: 'Pack' },
  { key: 'pack_logo_image', label: 'Logo' },
  { key: 'pedestal_image', label: 'Pedestal' },
] as const;

/** `withOpening`: the operator may set openings, so the pack's own one counts. */
function hasAnyOverride(c: PoolOfferConfig, withOpening: boolean): boolean {
  return (
    (withOpening && c.animation != null) ||
    Boolean(c.title) ||
    Boolean(c.badge_name) ||
    Boolean(c.description) ||
    c.estimated_value != null ||
    c.estimated_value_high != null ||
    c.estimated_value_max != null ||
    c.stated_rtp != null ||
    hasFixedRates(c) ||
    c.buyback_percent != null ||
    c.bg_image_override ||
    c.pack_image_override ||
    c.pack_logo_image_override ||
    c.pedestal_image_override
  );
}

export function ConfigsPage() {
  const [configs, setConfigs] = useState<PoolOfferConfig[]>([]);
  const [operator, setOperator] = useState<OperatorMe['operator'] | null>(null);
  const allowPricing = operator?.allow_pricing_overrides ?? false;
  const [tab, setTab] = useState<TabId>(readStoredTab);
  // Packs ticked in the pricing table; empty = defaults apply to every pack.
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const replaceConfig = (updated: PoolOfferConfig) =>
    setConfigs((prev) => prev.map((c) => (c.pool_offer_id === updated.pool_offer_id ? updated : c)));
  const openings = usePackOpenings({ configs, replaceConfig, flashSuccess, setError });

  useEffect(() => {
    void loadConfigs();
  }, []);

  async function loadConfigs() {
    try {
      setLoading(true);
      // Packs this operator may configure: the live ones plus the staged
      // (prepared, not yet published) ones — backend scope
      // PoolOffer.configurable_for_operator. Everything else stays hidden.
      const [result, me] = await Promise.all([
        operatorConsole.listPoolOfferConfigs({ availableOnly: true, includeStaged: true }),
        operatorConsole.me(),
      ]);
      setConfigs(result.pool_offer_configs);
      setOperator(me.operator);
      openings.readAccess(me);
    } catch (err) {
      setError(`Failed to load configs: ${err instanceof Error ? err.message : err}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleImageUpload(poolOfferId: number, slot: string, file: File) {
    try {
      setError(null);
      const updated = await operatorConsole.uploadPoolOfferConfigImage(poolOfferId, slot, file);
      setConfigs((prev) =>
        prev.map((c) => (c.pool_offer_id === poolOfferId ? updated : c)),
      );
      flashSuccess('Image uploaded');
    } catch (err) {
      setError(`Upload failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  async function handleImageDelete(poolOfferId: number, slot: string) {
    try {
      setError(null);
      const updated = await operatorConsole.deletePoolOfferConfigImage(poolOfferId, slot);
      setConfigs((prev) =>
        prev.map((c) => (c.pool_offer_id === poolOfferId ? updated : c)),
      );
      flashSuccess('Image reset');
    } catch (err) {
      setError(`Reset failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  async function handleTitleSave(poolOfferId: number, title: string) {
    try {
      setError(null);
      const updated = await operatorConsole.patchPoolOfferConfig(poolOfferId, { title });
      setConfigs((prev) =>
        prev.map((c) => (c.pool_offer_id === poolOfferId ? updated : c)),
      );
      flashSuccess(title === '' ? 'Name reset' : 'Name saved');
    } catch (err) {
      setError(`Name update failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  async function handleBadgeSave(poolOfferId: number, badgeName: string) {
    try {
      setError(null);
      const updated = await operatorConsole.patchPoolOfferConfig(poolOfferId, {
        badge_name: badgeName,
      });
      setConfigs((prev) =>
        prev.map((c) => (c.pool_offer_id === poolOfferId ? updated : c)),
      );
      flashSuccess(badgeName === '' ? 'Badge reset' : 'Badge saved');
    } catch (err) {
      setError(`Badge update failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  async function handleDescriptionSave(poolOfferId: number, description: string) {
    try {
      setError(null);
      const updated = await operatorConsole.patchPoolOfferConfig(poolOfferId, {
        description,
      });
      setConfigs((prev) =>
        prev.map((c) => (c.pool_offer_id === poolOfferId ? updated : c)),
      );
      flashSuccess(description === '' ? 'Description reset' : 'Description saved');
    } catch (err) {
      setError(`Description update failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  /** False when the backend refused the value (e.g. an RTP the pack's cards can't pay). */
  async function handlePricingSave(poolOfferId: number, patch: PricingPatch): Promise<boolean> {
    try {
      setError(null);
      const updated = await operatorConsole.patchPoolOfferConfig(poolOfferId, patch);
      setConfigs((prev) =>
        prev.map((c) => (c.pool_offer_id === poolOfferId ? updated : c)),
      );
      flashSuccess('Saved');
      return true;
    } catch (err) {
      setError(`Update failed: ${err instanceof Error ? err.message : err}`);
      return false;
    }
  }

  /** A level the pack's cards can't hold goes back to the fixed rates block, to be shown under that level. */
  async function handleFixedRatesSave(poolOfferId: number, patch: PricingPatch): Promise<FixedRatesResult> {
    try {
      setError(null);
      replaceConfig(await operatorConsole.patchPoolOfferConfig(poolOfferId, patch));
      flashSuccess('Saved');
      return { saved: true, refusal: null };
    } catch (err) {
      const refusal = refusedLevel(err);
      if (!refusal) setError(`Update failed: ${err instanceof Error ? err.message : err}`);
      return { saved: false, refusal };
    }
  }

  async function handleAvailabilityChange(
    poolOfferId: number,
    availability: 'enabled' | 'staged',
  ) {
    try {
      setError(null);
      const updated = await operatorConsole.setPoolOfferAvailability(poolOfferId, availability);
      setConfigs((prev) =>
        prev.map((c) => (c.pool_offer_id === poolOfferId ? updated : c)),
      );
      flashSuccess(availability === 'enabled' ? 'Pack published' : 'Pack unpublished');
    } catch (err) {
      setError(`Could not update the pack: ${err instanceof Error ? err.message : err}`);
    }
  }

  /** Resolves false when the backend did not take the change (feature not there yet). */
  async function handleDemoChange(poolOfferId: number, demoMode: boolean): Promise<boolean> {
    try {
      setError(null);
      const updated = await operatorConsole.patchPoolOfferConfig(poolOfferId, {
        demo_mode: demoMode,
      });
      // A backend without demo support drops the field and still answers 200,
      // so trust the echoed value, not the status.
      const applied = updated.demo_mode === demoMode;
      setConfigs((prev) =>
        prev.map((c) =>
          c.pool_offer_id === poolOfferId ? { ...updated, demo_mode: updated.demo_mode ?? false } : c,
        ),
      );
      if (applied) flashSuccess(demoMode ? 'Demo enabled' : 'Demo disabled');
      return applied;
    } catch (err) {
      setError(`Demo update failed: ${err instanceof Error ? err.message : err}`);
      return true;
    }
  }

  function handleGlobalPricingApplied(updated: PoolOfferConfig[]) {
    setConfigs(updated);
    flashSuccess('Applied to all packs');
  }

  function selectTab(id: TabId) {
    setTab(id);
    try {
      localStorage.setItem(TAB_STORAGE_KEY, id);
    } catch {
      // Storage blocked: the tab just won't be remembered.
    }
  }

  // Without the pricing flag there is nothing but assets to show.
  const activeTab: TabId = allowPricing ? tab : 'assets';
  const rtpCommon = commonValue(configs.map(rtpEffective));
  const buybackCommon = commonValue(configs.map(buybackEffective));
  // Ids of packs that left the list (e.g. after Apply) drop out of the selection.
  const liveSelection = selectedIds.filter((id) => configs.some((c) => c.pool_offer_id === id));
  const targetConfigs =
    liveSelection.length > 0
      ? configs.filter((c) => liveSelection.includes(c.pool_offer_id))
      : configs;
  const examplePrice =
    configs.find((c) => c.pool_offer_price != null && c.pool_offer_price > 0)?.pool_offer_price ??
    10;

  function flashSuccess(msg: string) {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 2500);
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-semibold text-[var(--co-text)]">Packs</h1>
          <p className="text-[12px] text-[var(--co-muted)]">
            {activeTab === 'pricing'
              ? 'How much value players get from each pack and what they earn selling cards back.'
              : 'Names, descriptions and images per pack — leave a slot empty to fall back to defaults. Packs marked “Not published” are yours to prepare: players only see them once you publish.'}
          </p>
        </div>
        {(error || success) && (
          <span
            className={`rounded px-2 py-1 text-[11px] font-medium ${
              error
                ? 'bg-[var(--co-error)] text-white'
                : 'bg-[var(--co-success)] text-white'
            }`}
          >
            {error ?? success}
          </span>
        )}
      </div>

      {allowPricing && (
        <div role="tablist" className="mb-4 flex gap-1 border-b border-[var(--co-border)]">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeTab === id}
              onClick={() => selectTab(id)}
              className={`-mb-px border-b-2 px-3 py-2 text-[13px] font-medium ${
                activeTab === id
                  ? 'border-[var(--co-brand)] text-[var(--co-text)]'
                  : 'border-transparent text-[var(--co-muted)] hover:text-[var(--co-text)]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-[13px] text-[var(--co-muted)]">Loading packs…</p>
      ) : configs.length === 0 ? (
        <p className="text-[13px] text-[var(--co-muted)]">No packs available for this operator.</p>
      ) : activeTab === 'pricing' ? (
        <>
          <GlobalPricingBar
            allowPricing={allowPricing}
            rtp={commonValue(targetConfigs.map(rtpEffective))}
            buyback={commonValue(targetConfigs.map(buybackEffective))}
            examplePrice={examplePrice}
            selectedIds={liveSelection}
            fixedRates={targetConfigs.some(hasFixedRates)}
            onApplied={handleGlobalPricingApplied}
          />
          <PricingTable
            configs={configs}
            defaultRtp={rtpCommon.value}
            defaultBuyback={buybackCommon.value}
            onSave={handlePricingSave}
            operator={operator}
            onSaveFixedRates={handleFixedRatesSave}
            bucketsAccess={operator}
            onBucketsError={setError}
            selectedIds={liveSelection}
            onSelectionChange={setSelectedIds}
          />
        </>
      ) : (
        <div className="flex flex-col gap-3">
          {configs.map((config) => (
            <div
              key={config.pool_offer_id}
              className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-3"
            >
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="font-mono text-[11px] text-[var(--co-muted)]">
                  #{config.pool_offer_id}
                </span>
                <h2 className="text-[14px] font-semibold text-[var(--co-text)]">
                  {config.title ?? config.pool_offer_title}
                </h2>
                {(isCustomPack(config) || config.pool_offer_price != null) && (
                  <span className="font-mono text-[12px] text-[var(--co-muted)]">{priceLabel(config)}</span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {hasAnyOverride(config, openings.access != null) && (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                      Customized
                    </span>
                  )}
                  <PublishControl
                    availability={config.availability}
                    restockingHint={restockingHint(config)}
                    onChange={(availability) =>
                      handleAvailabilityChange(config.pool_offer_id, availability)
                    }
                  />
                </div>
              </div>

              {/* Name + Badge side by side, Description full width, images in one row. */}
              <div className="flex flex-col gap-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextSetting
                    label="Name"
                    inputLabel="Pack name"
                    resetLabel="Reset pack name to default"
                    value={config.title}
                    defaultValue={config.pool_offer_title}
                    maxLength={255}
                    onSave={(title) => handleTitleSave(config.pool_offer_id, title)}
                    onReset={() => handleTitleSave(config.pool_offer_id, '')}
                  />
                  <TextSetting
                    label="Badge"
                    inputLabel="Badge name"
                    resetLabel="Reset badge name to default"
                    value={config.badge_name}
                    defaultValue={config.pool_offer_badge_name}
                    maxLength={255}
                    onSave={(badgeName) => handleBadgeSave(config.pool_offer_id, badgeName)}
                    onReset={() => handleBadgeSave(config.pool_offer_id, '')}
                  />
                </div>
                <TextSetting
                  label="Description"
                  inputLabel="Pack description"
                  resetLabel="Reset description to default"
                  value={config.description}
                  defaultValue={config.pool_offer_description}
                  maxLength={2000}
                  multiline
                  onSave={(description) =>
                    handleDescriptionSave(config.pool_offer_id, description)
                  }
                  onReset={() => handleDescriptionSave(config.pool_offer_id, '')}
                />

                <DemoToggle
                  enabled={config.demo_mode}
                  onChange={(demoMode) => handleDemoChange(config.pool_offer_id, demoMode)}
                />

                {openings.access && (
                  <OpeningAnimationSetting
                    pack={config}
                    global={openings.global}
                    loading={openings.loading}
                    onEdit={() => openings.edit(config.pool_offer_id)}
                    onReset={() => openings.reset(config.pool_offer_id)}
                  />
                )}

                <section aria-label="Images" className="flex flex-col gap-2">
                  <div>
                    <h3 className="text-[12px] font-medium text-[var(--co-text)]">Images</h3>
                    <p className="text-[11px] text-[var(--co-muted)]">
                      Click or drop a file to replace. Reset returns to the default image.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {IMAGE_SLOTS.map(({ key, label }) => {
                      const imageUrl = config[`${key}_url` as keyof PoolOfferConfig] as
                        string | null;
                      const isOverride = config[
                        `${key}_override` as keyof PoolOfferConfig
                      ] as boolean;
                      return (
                        <ImageSlot
                          key={key}
                          label={label}
                          imageUrl={imageUrl}
                          isOverride={isOverride}
                          variant="tile"
                          onUpload={(file) => handleImageUpload(config.pool_offer_id, key, file)}
                          onClear={() => handleImageDelete(config.pool_offer_id, key)}
                        />
                      );
                    })}
                  </div>
                </section>
              </div>
            </div>
          ))}
        </div>
      )}

      {openings.editing && openings.access && (
        <PackOpeningModal
          key={openings.editing.pool_offer_id}
          pack={openings.editing}
          template={openings.access.template}
          global={openings.global}
          canEditGlobal={openings.access.canEditGlobal}
          onClose={openings.close}
          onSaved={openings.saved}
        />
      )}
    </div>
  );
}
